import { Button, formatMoney } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { type DropdownItem, DropdownMenu } from "@majitap/ui/arc/dropdown-menu/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronRight,
  CircleX,
  ExternalLink,
  FileText,
  Image,
  Pause,
  Play,
  Redo2,
  RotateCcw,
  Signature,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { type DeliveryRecord, deliveryHref, deliveryQuery, tripOf } from "../../deliveries";
import {
  actionsFor,
  type BoardRow,
  clockOf,
  dispatchActions,
  durationOf,
  litresOf,
  nextStage,
  type ResumeTo,
  STAGE_ACTION,
  sinceOf,
} from "../../dispatch";
import { messageFor } from "../../error-copy";
import { DELIVERY_STATUS, PAYMENT_STATUS } from "../../labels";
import { ageOf, kmBetween, truckPositionsQuery, truckState } from "../../tracking";
import { LogIncidentDialog } from "../incidents/dialogs";
import {
  AssignDrawer,
  CompleteDialog,
  OfferDialog,
  ProgressDialog,
  ReasonDialog,
  ResetDialog,
  ResumeDialog,
} from "./actions";
import styles from "./board.module.css";
import { Fact, PaneHead, PaneSection, Person } from "./pane";
import { RerouteDialog } from "./reroute";

type Action =
  | "progress"
  | "complete"
  | "assign"
  | "unassign"
  | "suspend"
  | "reroute"
  | "offer"
  | "incident"
  | "reset"
  | `resume:${ResumeTo}`;

const UNDER_WAY = new Set(["accepted", "at_fillsite", "in_transit", "at_customer"]);
const litresText = (l: number) => `${l.toLocaleString("en-KE")} L`;

/**
 * Following a delivery on the board (`Console / Dispatch / Assigned`, `… / In progress`,
 * `… / In progress / Suspended`, `… / Done`): what needs a look, then what the tab is about (the
 * plan, the trip, what next, the proof and money), the crew to call, and the next step.
 */
export function FollowPanel({ row, onClose }: { row: BoardRow | null; onClose: () => void }) {
  const delivery = useQuery({ ...deliveryQuery(row?.id ?? ""), enabled: Boolean(row), refetchInterval: 15_000 });
  const format = useCompanyFormat();
  const [open, setOpen] = useState<Action | null>(null);
  if (!row) {
    return (
      <section className={styles.side} aria-label="The selected delivery">
        <PaneHead title="Nothing selected" sub="Select a delivery in the list or on the map to follow it." />
      </section>
    );
  }
  const d = delivery.data;
  const close = () => setOpen(null);
  const tz = format.timezone;
  const sub = [
    row.order.customer_name,
    row.location?.label,
    row.geofence?.name,
    UNDER_WAY.has(row.status) ? row.order.order_number : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className={styles.side} aria-label={`Follow ${row.delivery_number}`}>
      <PaneHead
        title={row.delivery_number}
        status={DELIVERY_STATUS[row.status]}
        sub={sub}
        open={deliveryHref(row.order.id, row.id)}
        onClose={onClose}
      />
      <div className={styles.sideBody}>
        <Attention row={row} d={d} />
        {delivery.error ? (
          <Alert tone="danger" title="Couldn't load the delivery">
            {messageFor(errorOf(delivery.error))}
          </Alert>
        ) : null}
        {d ? <Body row={row} d={d} tz={tz} onPick={setOpen} /> : <p className={styles.small}>Loading…</p>}
      </div>
      {d ? <Footer row={row} d={d} onPick={setOpen} /> : null}
      {d ? <Dialogs d={d} open={open} close={close} /> : null}
    </section>
  );
}

/** What needs a look, at the top: held at the customer, not accepted, or slow at its step. */
function Attention({ row, d }: { row: BoardRow; d: DeliveryRecord | undefined }) {
  const format = useCompanyFormat();
  const positions = useQuery(truckPositionsQuery);
  const driver = row.assignment?.driver?.name ?? "The driver";
  if (row.status === "suspended") {
    const held = d?.events.find((e) => e.to_status === "suspended");
    const p = d?.truck_position;
    const km = p && !p.stale ? kmBetween(p, d?.location) : null;
    const truck = !p
      ? null
      : p.stale
        ? `The truck hasn't been seen for ${ageOf(p.recorded_at)}.`
        : km !== null && km < 0.3
          ? `The truck is still there (seen ${ageOf(p.recorded_at)} ago).`
          : `The truck is ${km ?? "?"} km away (seen ${ageOf(p.recorded_at)} ago).`;
    return (
      <div className={styles.callout} data-tone="danger">
        <strong className={styles.calloutTitle}>
          <Pause size={16} aria-hidden /> {`Held at the customer · ${sinceOf(row.status_since)}`}
        </strong>
        <p>
          {held?.reason ? `“${held.reason}” ` : ""}
          {held ? `${held.actor?.name ?? driver} at ${clockOf(held.occurred_at, format.timezone)}. ` : ""}
          {truck}
        </p>
      </div>
    );
  }
  const truck = positions.data?.data.find((p) => p.truck_id === row.assignment?.truck?.id);
  const stillFor =
    truck && !truck.stale && truckState(truck) !== "moving" && truck.moved_at ? sinceOf(truck.moved_at) : null;
  if (row.flags.not_accepted) {
    return (
      <div className={styles.callout} data-tone="warning">
        <strong className={styles.calloutTitle}>
          <TriangleAlert size={16} aria-hidden /> {`Not accepted after ${sinceOf(row.status_since)}`}
        </strong>
        <p>{`${driver} hasn't accepted it.${stillFor ? ` The truck hasn't moved for ${stillFor}.` : ""} Call them, or give it to a free driver.`}</p>
      </div>
    );
  }
  if (row.flags.slow) {
    return (
      <div className={styles.callout} data-tone="warning">
        <strong className={styles.calloutTitle}>
          <TriangleAlert size={16} aria-hidden /> {`${sinceOf(row.status_since)} at this step`}
        </strong>
        <p>
          {`${row.usual_minutes ? `Usually ${row.usual_minutes} min here. ` : "Longer than the company's limit. "}Call ${driver} to see what's holding it.`}
        </p>
      </div>
    );
  }
  if (row.flags.late) {
    return (
      <div className={styles.callout} data-tone="warning">
        <strong className={styles.calloutTitle}>
          <TriangleAlert size={16} aria-hidden /> Running late
        </strong>
        <p>It should be on its way by now.</p>
      </div>
    );
  }
  return null;
}

function Body({ row, d, tz, onPick }: { row: BoardRow; d: DeliveryRecord; tz: string; onPick: (a: Action) => void }) {
  const format = useCompanyFormat();
  const a = d.assignment;
  const litres = litresOf(row);
  const crew = <Crew d={d} />;
  if (d.status === "assigned") {
    const km = a?.fillsite ? kmBetween(a.fillsite, d.location) : null;
    return (
      <>
        <PaneSection title="Plan">
          <dl className={styles.facts}>
            <Fact
              label="Starts"
              value={d.scheduled_at ? clockOf(d.scheduled_at, tz) : "As soon as possible"}
              note={`ordered ${clockOf(d.created_at, tz)}`}
            />
            {a?.fillsite ? (
              <Fact
                label="Fill site"
                value={a.fillsite.name}
                note={km === null ? undefined : `${km} km from the customer`}
              />
            ) : null}
            {a?.truck ? (
              <Fact
                label="Truck"
                value={`${a.truck.plate_number} · ${litresText(a.truck.capacity_litres)}`}
                note={litres ? `fits the ${litresText(litres)} order` : undefined}
              />
            ) : null}
          </dl>
        </PaneSection>
        {crew}
      </>
    );
  }
  if (d.status === "suspended") {
    const options: [ResumeTo | "reroute", string, string, ReactNode, boolean][] = [
      ["previous", "Resume", "Carry on at the customer", <Play key="i" size={16} />, false],
      ["reroute", "Reroute", "To another of their places, or another client", <Redo2 key="i" size={16} />, false],
      [
        "pending",
        "Back to planning",
        "Needs a new fill site, truck and driver",
        <RotateCcw key="i" size={16} />,
        false,
      ],
      ["failed", "Mark failed", "The customer couldn't take it", <CircleX key="i" size={16} />, true],
    ];
    return (
      <>
        <PaneSection title="What next">
          <ul className={styles.nextList}>
            {options.map(([key, label, help, icon, danger]) => (
              <li key={key}>
                <button
                  type="button"
                  className={styles.nextOption}
                  data-danger={danger || undefined}
                  onClick={() => onPick(key === "reroute" ? "reroute" : `resume:${key}`)}
                >
                  <span className={styles.nextIcon} aria-hidden>
                    {icon}
                  </span>
                  <span className={styles.optionBody}>
                    <strong>{label}</strong>
                    <span className={styles.small}>{help}</span>
                  </span>
                  <ChevronRight size={16} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </PaneSection>
        <Crew d={d} title="Call" />
      </>
    );
  }
  if (d.status === "delivered") {
    const assigned = d.events.find((e) => e.to_status === "assigned")?.occurred_at;
    const atSite = d.events.find((e) => e.to_status === "at_fillsite")?.occurred_at;
    const left = d.events.find((e) => e.to_status === "in_transit")?.occurred_at;
    const checks = (d.proof?.checklist ?? []).filter((c) => c.kind === "yes_no" && c.bool_value).map((c) => c.label);
    const files = (d.proof?.checklist ?? []).filter((c) => (c.kind === "photo" || c.kind === "signature") && c.file_id);
    const due = d.scheduled_at;
    const lateBy = due && d.delivered_at ? Date.parse(d.delivered_at) - Date.parse(due) : null;
    return (
      <>
        <PaneSection title="Proof">
          {d.proof ? (
            <dl className={styles.facts}>
              <Fact
                label="Received by"
                value={d.proof.received_by_name ?? "Not given"}
                note={d.proof.signed_at ? `signed ${clockOf(d.proof.signed_at, tz)}` : undefined}
              />
              {checks.length ? <Fact label="Checks" value={checks.join(" · ")} /> : null}
            </dl>
          ) : (
            <p className={styles.small}>No proof recorded.</p>
          )}
          {files.length ? (
            <div className={styles.tiles}>
              {files.map((f) => (
                <Link key={f.item_id} to={deliveryHref(d.order.id, d.id)} className={styles.tile}>
                  {f.kind === "photo" ? <Image size={18} aria-hidden /> : <Signature size={18} aria-hidden />}
                  <span>{f.label}</span>
                </Link>
              ))}
            </div>
          ) : null}
        </PaneSection>
        <PaneSection title="Trip">
          <dl className={styles.facts}>
            {assigned && d.delivered_at ? (
              <Fact
                label="Took"
                value={[
                  durationOf(Date.parse(d.delivered_at) - Date.parse(assigned)),
                  lateBy === null ? null : lateBy > 0 ? `${durationOf(lateBy)} late` : "on time",
                ]
                  .filter(Boolean)
                  .join(" · ")}
                note={`assigned ${clockOf(assigned, tz)} · delivered ${clockOf(d.delivered_at, tz)}`}
              />
            ) : null}
            {a?.fillsite ? (
              <Fact
                label="Fill site"
                value={[a.fillsite.name, atSite && left ? durationOf(Date.parse(left) - Date.parse(atSite)) : null]
                  .filter(Boolean)
                  .join(" · ")}
              />
            ) : null}
          </dl>
        </PaneSection>
        <PaneSection title="Money">
          <dl className={styles.facts}>
            <Fact
              label="Charged"
              value={formatMoney(d.receivable_total, format)}
              note={`${d.order.order_number} · ${PAYMENT_STATUS[d.order.payment_status][0].toLowerCase()}`}
            />
            <Fact
              label="Margin"
              value={formatMoney(d.margin, format)}
              note={`costs ${formatMoney(d.payable_total, format)}`}
            />
          </dl>
        </PaneSection>
      </>
    );
  }
  // under way, or closed: the trip so far
  return (
    <>
      <Trip d={d} tz={tz} />
      {UNDER_WAY.has(d.status) ? crew : null}
      {d.status === "rerouted" && (d.replaced_by || d.load_to) ? (
        <p className={styles.small}>
          {d.load_to
            ? `Its load went to ${d.load_to.delivery_number}.`
            : `Replaced by ${d.replaced_by?.delivery_number}.`}
        </p>
      ) : null}
    </>
  );
}

/** The trip as a vertical timeline (`Console / Dispatch / In progress`, Trip). */
function Trip({ d, tz }: { d: DeliveryRecord; tz: string }) {
  const { steps } = tripOf(d);
  const driver = d.assignment?.driver?.name;
  const eventOf = (key: string) => d.events.find((e) => e.to_status === key);
  const shown = steps.filter((s) => s.key !== "pending" && s.key !== "fillsite_assigned");
  return (
    <PaneSection title="Trip">
      <ol className={styles.timeline}>
        {shown.map((s, i) => {
          const e = eventOf(s.key);
          const next = shown[i + 1];
          const nextAt = next ? eventOf(next.key)?.occurred_at : undefined;
          let note: string | null = null;
          if (s.state === "now" && s.at) {
            note = [
              `${sinceOf(s.at)} so far`,
              s.key === "in_transit" && d.eta
                ? // number-ok: metres to km, not money
                  `${(d.eta.distance_m / 1000).toFixed(1)} km by road to go`
                : s.key === "in_transit" && d.km_to_go !== null
                  ? `${d.km_to_go} km to go`
                  : null,
            ]
              .filter(Boolean)
              .join(" · ");
          } else if (s.state === "done" && s.key === "at_fillsite" && s.at && nextAt) {
            note = `filled in ${durationOf(Date.parse(nextAt) - Date.parse(s.at))}`;
          } else if (s.state === "done" && e) {
            note = !e.actor ? "automatically" : e.actor.name === driver ? "from the driver app" : `by ${e.actor.name}`;
          }
          return (
            <li key={s.key} className={styles.timelineStep} data-state={s.state}>
              <span className={styles.timelineDot} aria-hidden />
              <span className={styles.optionBody}>
                <span className={styles.timelineLabel}>{s.label}</span>
                {note ? <span className={styles.small}>{note}</span> : null}
              </span>
              {!s.at && d.eta && s.key === "at_customer" ? (
                <span className={d.eta.late ? styles.etaLate : styles.eta}>~{clockOf(d.eta.arrives_at, tz)}</span>
              ) : (
                <span className={styles.small}>{s.at ? clockOf(s.at, tz) : "—"}</span>
              )}
            </li>
          );
        })}
      </ol>
    </PaneSection>
  );
}

/** The driver and the customer's contact, each with a call button. */
function Crew({ d, title = "Crew" }: { d: DeliveryRecord; title?: string }) {
  const a = d.assignment;
  const place = d.location;
  const contact = place?.contact_name ?? d.order.customer.name;
  const phone = place?.contact_phone ?? d.order.customer.phone_number;
  return (
    <PaneSection title={title}>
      <div className={styles.people}>
        {a?.driver ? (
          <Person
            name={a.driver.name}
            detail={["Driver", a.truck?.plate_number, a.truck ? litresText(a.truck.capacity_litres) : null]
              .filter(Boolean)
              .join(" · ")}
            phone={a.driver.phone_number}
          />
        ) : null}
        {a?.assistant ? <Person name={a.assistant.name} detail="Assistant" phone={a.assistant.phone} /> : null}
        <Person name={contact} detail="Customer contact" phone={phone} />
      </div>
    </PaneSection>
  );
}

/** The next step first, then the rest (`… / Assigned`: Mark accepted, Reassign, Unassign). */
function Footer({ row, d, onPick }: { row: BoardRow; d: DeliveryRecord; onPick: (a: Action) => void }) {
  const navigate = useNavigate();
  const can = actionsFor(d.status, d.dispatch_mode);
  const planned = Boolean(d.assignment?.driver);
  const stage = planned ? nextStage(d.status, Boolean(d.assignment?.fillsite)) : null;
  const openDelivery = () => navigate({ to: deliveryHref(d.order.id, d.id) });
  const openOrder = () => navigate({ to: "/orders/$orderId", params: { orderId: d.order.id } });
  const note = <p className={styles.small}>For a driver who can't use the app. The timeline names you.</p>;

  if (d.status === "suspended") {
    return (
      <footer className={styles.sideFoot}>
        <div className={styles.buttonRow}>
          <Button variant="secondary" onClick={() => onPick("incident")}>
            Log an incident
          </Button>
          <Button variant="secondary" onClick={openDelivery}>
            <ExternalLink size={16} aria-hidden /> Open
          </Button>
        </div>
      </footer>
    );
  }
  if (d.status === "delivered" || !planned || ["failed", "cancelled", "rerouted"].includes(d.status)) {
    return (
      <footer className={styles.sideFoot}>
        <Button className={styles.wide} onClick={openDelivery}>
          Open the delivery
        </Button>
        <div className={styles.buttonRow}>
          {can.incident ? (
            <Button variant="secondary" onClick={() => onPick("incident")}>
              Log an incident
            </Button>
          ) : null}
          <Button variant="secondary" onClick={openOrder}>
            <FileText size={16} aria-hidden /> Open order
          </Button>
        </div>
        {d.status === "delivered" ? (
          <p className={styles.small}>A complaint can come after delivery: log it here.</p>
        ) : null}
      </footer>
    );
  }
  const primary = stage ? (
    <Button className={styles.wide} onClick={() => onPick("progress")}>
      {STAGE_ACTION[stage]}
    </Button>
  ) : can.complete ? (
    <Button className={styles.wide} onClick={() => onPick("complete")}>
      Mark delivered
    </Button>
  ) : null;
  if (row.status === "assigned") {
    return (
      <footer className={styles.sideFoot}>
        {primary}
        <div className={styles.buttonRow}>
          {can.assign ? (
            <Button variant="secondary" onClick={() => onPick("assign")}>
              Reassign
            </Button>
          ) : null}
          {can.unassign ? (
            <Button variant="secondary" onClick={() => onPick("unassign")}>
              Unassign
            </Button>
          ) : null}
          {can.reroute ? (
            <Button variant="secondary" onClick={() => onPick("reroute")}>
              Reroute
            </Button>
          ) : null}
        </div>
        {note}
      </footer>
    );
  }
  const more: DropdownItem[] = [];
  if (stage && can.complete) more.push({ label: "Mark delivered", onSelect: () => onPick("complete") });
  if (can.suspend) more.push({ label: "Suspend", onSelect: () => onPick("suspend"), destructive: true });
  if (can.offer) more.push({ label: "Offer to partners", onSelect: () => onPick("offer") });
  if (can.reroute) more.push({ label: "Reroute", onSelect: () => onPick("reroute") });
  if (can.reset)
    more.push({
      label: "Reset to pending",
      onSelect: () => onPick("reset"),
      destructive: true,
      separatorBefore: more.length > 0,
    });
  return (
    <footer className={styles.sideFoot}>
      {primary}
      <div className={styles.buttonRow}>
        <Button variant="secondary" onClick={() => onPick("incident")}>
          <TriangleAlert size={16} aria-hidden /> Log an incident
        </Button>
        {more.length ? <DropdownMenu label="More" items={more} /> : null}
      </div>
      {note}
    </footer>
  );
}

function Dialogs({ d, open, close }: { d: DeliveryRecord; open: Action | null; close: () => void }) {
  const stage = d.assignment?.driver ? nextStage(d.status, Boolean(d.assignment?.fillsite)) : null;
  if (!open) return null;
  if (open.startsWith("resume:")) {
    return <ResumeDialog delivery={d} initial={open.slice("resume:".length) as ResumeTo} onClose={close} />;
  }
  switch (open) {
    case "progress":
      return stage ? <ProgressDialog delivery={d} to={stage} onClose={close} /> : null;
    case "complete":
      return <CompleteDialog delivery={d} onClose={close} />;
    case "assign":
      return <AssignDrawer delivery={d} onClose={close} />;
    case "unassign":
      return (
        <ReasonDialog
          title={`Unassign ${d.delivery_number}?`}
          description="The truck and driver come off it. A water load keeps its fill site."
          action="Unassign"
          run={(reason) => dispatchActions.unassign(d.id, reason)}
          onClose={close}
        />
      );
    case "suspend":
      return (
        <ReasonDialog
          title={`Suspend ${d.delivery_number}?`}
          description="It waits at the customer until you resume or reroute it."
          action="Suspend"
          danger
          scope="delivery_suspended"
          run={(reason, reasonId) => dispatchActions.suspend(d.id, reason, reasonId)}
          onClose={close}
        />
      );
    case "reroute":
      return <RerouteDialog delivery={d} onClose={close} />;
    case "offer":
      return <OfferDialog delivery={d} onClose={close} />;
    case "incident":
      return <LogIncidentDialog delivery={d} onClose={close} />;
    case "reset":
      return <ResetDialog delivery={d} onClose={close} />;
    default:
      return null;
  }
}
